/**
 * ChannelSurf — the site as a TV you can actually operate.
 *
 *   1–4        tune straight to a world (the card's own grow + channel wipe)
 *   ↑ / ↓      channel up / down through the worlds
 *   0          home / hero
 *   dice       "AUTO-SCAN" (the randomizer)
 *
 * Every action flashes a TV-style on-screen display in the corner —
 * "CH 02 · TRAVEL" — the way a set shows the channel number when you press
 * the remote. Keys are ignored while typing in an input (Chotbat) and while
 * a world is open (each world owns its own keys).
 */

const OSD_HOLD_MS = 1400;

export class ChannelSurf {
  /**
   * @param {Object} o
   * @param {HTMLElement[]} o.cards — the carousel cards, in channel order
   */
  constructor({ cards }) {
    this.cards = cards;
    this.channels = cards.map((c, i) => ({
      n: i + 1,
      world: c.dataset.world,
      name: c.querySelector('.card__title')?.innerText.trim() || c.dataset.world,
      card: c,
    }));
    this.current = 0; // 0 = home
    this._osd = this._mountOSD();
    this._hideT = null;

    document.addEventListener('keydown', this._onKey);
    document.addEventListener('randomize', () => this.show('AUTO-SCAN', 'searching…', { accent: true }));
    document.addEventListener('bg:auto-change', () => this.show('AUTO', 'next program'));
    // Reflect in-place world opens/closes made by other means (card clicks, menu)
    new MutationObserver(() => {
      const open = document.body.classList.contains('is-world-open');
      if (!open && this.current !== 0) this.current = 0;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  _mountOSD() {
    const el = document.createElement('div');
    el.className = 'osd';
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = '<span class="osd__ch"></span><span class="osd__name"></span>';
    document.body.appendChild(el);
    return el;
  }

  /** Flash the OSD. */
  show(ch, name, { accent = false } = {}) {
    this._osd.querySelector('.osd__ch').textContent = ch;
    this._osd.querySelector('.osd__name').textContent = name || '';
    this._osd.classList.toggle('osd--accent', accent);
    this._osd.classList.remove('is-on');
    void this._osd.offsetWidth; // restart the flicker-in
    this._osd.classList.add('is-on');
    clearTimeout(this._hideT);
    this._hideT = setTimeout(() => this._osd.classList.remove('is-on'), OSD_HOLD_MS);
  }

  _onKey = (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (document.body.classList.contains('is-world-open')) return;
    if (document.body.classList.contains('is-menu-open')) return;

    if (/^[0-9]$/.test(e.key)) {
      const n = Number(e.key);
      if (n === 0) return this.tune(0);
      if (n <= this.channels.length) return this.tune(n);
      return this.show(`CH ${String(n).padStart(2, '0')}`, 'no signal');
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Only when the page isn't being scrolled with the keys (i.e. hero in view)
      if (window.scrollY > window.innerHeight * 0.6) return;
      e.preventDefault();
      const next = e.key === 'ArrowUp'
        ? (this.current % this.channels.length) + 1
        : (this.current - 2 + this.channels.length) % this.channels.length + 1;
      return this.tune(next);
    }
  };

  /** Tune to channel n (0 = home). */
  tune(n) {
    if (n === 0) {
      this.current = 0;
      this.show('CH 00', 'home');
      if (window.lenis?.scrollTo) window.lenis.scrollTo(0, { duration: 1 }); else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const ch = this.channels[n - 1];
    if (!ch) return;
    this.current = n;
    this.show(`CH ${String(n).padStart(2, '0')}`, ch.name);
    // Bring the card on screen, then open it exactly as a tap would
    const rect = ch.card.getBoundingClientRect();
    const onScreen = rect.top >= 0 && rect.bottom <= window.innerHeight;
    const go = () => ch.card.click();
    if (onScreen) {
      setTimeout(go, 260);
    } else {
      const worlds = document.getElementById('worlds');
      if (window.lenis?.scrollTo && worlds) window.lenis.scrollTo(worlds, { duration: 0.7, offset: -4 });
      else worlds?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(go, 820);
    }
  }

  destroy() {
    document.removeEventListener('keydown', this._onKey);
    this._osd.remove();
  }
}
